import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import AddGeographicalSoil from "./AddGeographicalSoil";
import {
  getAllGeographicalSoil,
  deleteGeographicalSoil,
  getSoilMasterMetadata,
} from "../lib/api";

const LEVEL_CONFIG = {
  1: { name: "Country", icon: "mdi:earth" },
  2: { name: "Province", icon: "mdi:map-marker" },
  3: { name: "District", icon: "mdi:office-building" },
  4: { name: "Commune", icon: "mdi:home-group" },
  5: { name: "Village", icon: "mdi:home" },
};

const GeographicalSoilList = () => {
  const getToken = () => localStorage.getItem("token") || localStorage.getItem("authToken");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // State
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metaTextures, setMetaTextures] = useState([]);

  // Filtering & Pagination State
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLevelFilter, setSelectedLevelFilter] = useState("");
  const [selectedTextureFilter, setSelectedTextureFilter] = useState("");
  const [conditionFilter, setConditionFilter] = useState("all");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Drawer & Modal States
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState(null);

  // Delete State
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  // Fetch Soil Master Metadata
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const token = getToken();
        const res = await getSoilMasterMetadata(token);
        if (res?.data?.textures) {
          setMetaTextures(res.data.textures);
        }
      } catch (e) {
        console.warn("Could not load soil metadata:", e);
      }
    };
    fetchMeta();
  }, []);

  // Reset page on filter/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedLevelFilter, selectedTextureFilter, conditionFilter, entriesPerPage]);

  // Fetch List Records
  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken();

      const params = {
        page: currentPage,
        limit: entriesPerPage,
        search: searchTerm.trim() || undefined,
        level_type: selectedLevelFilter || undefined,
        texture: selectedTextureFilter || undefined,
      };

      if (conditionFilter === "acidic") params.is_acidic = true;
      if (conditionFilter === "saline_sodic") params.is_saline_sodic = true;
      if (conditionFilter === "calcareous") params.is_calcareous = true;
      if (conditionFilter === "heavy_metals") params.heavy_metals_detected = true;

      const res = await getAllGeographicalSoil(params, token);
      const data = res?.data || [];
      setRecords(data);
      if (res?.pagination?.total !== undefined) {
        setTotalRecords(res.pagination.total);
      } else {
        setTotalRecords(data.length);
      }
    } catch (err) {
      console.error("Error fetching geographical soil:", err);
      toast.error(err?.response?.data?.message || "Failed to load geographical soil records.");
    } finally {
      setLoading(false);
    }
  }, [currentPage, entriesPerPage, searchTerm, selectedLevelFilter, selectedTextureFilter, conditionFilter]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Handlers
  const handleAddNew = () => {
    setRecordToEdit(null);
    setIsDrawerOpen(true);
  };

  const handleEdit = (record) => {
    setRecordToEdit(record);
    setIsDrawerOpen(true);
  };

  const confirmDelete = (record) => {
    setRecordToDelete(record);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deletingId) return;
    setShowDeleteModal(false);
    setRecordToDelete(null);
  };

  const handleDelete = async () => {
    if (!recordToDelete) return;
    try {
      setDeletingId(recordToDelete.id);
      const token = getToken();
      const res = await deleteGeographicalSoil(recordToDelete.id, token);
      toast.success(res?.message || "Soil record deleted successfully (backup snapshot saved).");
      setShowDeleteModal(false);
      setRecordToDelete(null);
      fetchRecords();
    } catch (err) {
      console.error("Error deleting soil record:", err);
      toast.error(err?.response?.data?.message || err.message || "Failed to delete record.");
    } finally {
      setDeletingId(null);
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedLevelFilter("");
    setSelectedTextureFilter("");
    setConditionFilter("all");
    setCurrentPage(1);
  };

  // Pagination calculation
  const totalPages = entriesPerPage === -1 ? 1 : Math.max(1, Math.ceil(totalRecords / entriesPerPage));
  const startIndex = entriesPerPage === -1 ? 0 : (currentPage - 1) * entriesPerPage;
  const endIndex = entriesPerPage === -1 ? records.length : Math.min(startIndex + entriesPerPage, totalRecords);

  const hasActiveFilters =
    !!searchTerm || !!selectedLevelFilter || !!selectedTextureFilter || conditionFilter !== "all";

  return (
    <>
      <div className="card p-0 radius-12">
        {/* ─── Header ─────────────────────────────────────────────────── */}
        <div className="card-header border-bottom bg-base py-16 px-24">
          <div className="d-flex align-items-center justify-content-between gap-3">
            {/* Left group — always one line */}
            <div className="d-flex align-items-center gap-3 flex-nowrap">
              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <span className="text-sm fw-medium text-secondary-light text-nowrap">Show</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 76 }}
                  value={entriesPerPage}
                  onChange={(event) => setEntriesPerPage(Number(event.target.value))}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={-1}>All</option>
                </select>
              </div>

              <div className="navbar-search flex-shrink-0" style={{ minWidth: 240 }}>
                <input
                  className="bg-base h-40-px w-auto"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search entity, location, texture..."
                />
                <Icon icon="ion:search-outline" className="icon" />
              </div>

              {hasActiveFilters && (
                <button
                  type="button"
                  className="btn btn-sm btn-light px-12 py-10 radius-8 d-flex align-items-center gap-2 flex-shrink-0"
                  onClick={resetFilters}
                  title="Reset filters"
                >
                  <Icon icon="lucide:rotate-ccw" style={{ fontSize: 15 }} />
                  <span className="text-sm text-nowrap">Reset</span>
                </button>
              )}
            </div>

            {/* Right — Add button */}
            <button
              onClick={handleAddNew}
              className="btn btn-yellow text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2 flex-shrink-0"
            >
              <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
              <span className="text-nowrap">Add Soil Data</span>
            </button>
          </div>
        </div>

        {/* ─── Table ──────────────────────────────────────────────────── */}
        {loading ? (
          <div className="text-center py-4 d-flex align-items-center justify-content-center gap-2">
            <span className="spinner-border text-primary spinner-border-sm" role="status" />
            <span className="text-secondary-light">Loading geographical soil records...</span>
          </div>
        ) : (
          <>
            <div className="table-responsive p-16">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr>
                    <th>Entity & Location</th>
                    <th>Texture</th>
                    <th>pH / EC</th>
                    <th>Org. C</th>
                    <th>N-P-K (kg/ha)</th>
                    <th className="text-center">Conditions</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {records.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4 text-muted">
                        {hasActiveFilters
                          ? "No records match your filters. Try resetting."
                          : "No geographical soil records found. Add the first one."}
                      </td>
                    </tr>
                  ) : (
                    records.map((item) => {
                      const lvlConfig = LEVEL_CONFIG[item.level_type] || {
                        name: `Level ${item.level_type}`,
                        icon: "mdi:tag",
                      };

                      const isNormal =
                        !item.is_acidic &&
                        !item.is_saline_sodic &&
                        !item.is_calcareous &&
                        !item.heavy_metals_detected;

                      return (
                        <tr key={item.id}>
                          <td>
                            <div className="text-md fw-medium text-primary-light">
                              {item.entity_name || item.level_id}
                            </div>
                            {item.geography && (
                              <div className="text-xs text-secondary-light mt-1">
                                {[
                                  item.geography.CountryName,
                                  item.level_type > 1 ? item.geography.ProvinceName : null,
                                  item.level_type > 2 ? item.geography.DistrictName : null,
                                  item.level_type > 3 ? item.geography.CommuneName : null,
                                  item.level_type > 4 ? item.geography.VillageName : null,
                                ]
                                  .filter(Boolean)
                                  .join(" › ")}
                              </div>
                            )}
                          </td>

                          <td>
                            <span className="text-md fw-normal text-secondary-light">
                              {item.texture || "—"}
                            </span>
                            {item.bulk_density_g_cm3 && (
                              <div className="text-xs text-secondary-light">
                                BD: {item.bulk_density_g_cm3} g/cm³
                              </div>
                            )}
                          </td>

                          <td>
                            <div className="text-md fw-medium text-primary-light">
                              pH {item.ph ?? "—"}
                            </div>
                            <div className="text-xs text-secondary-light">
                              EC {item.ec_ds_m ?? "—"} dS/m
                            </div>
                          </td>

                          <td>
                            <span className="text-md fw-normal text-secondary-light">
                              {item.organic_carbon_pct != null ? `${item.organic_carbon_pct}%` : "—"}
                            </span>
                          </td>

                          <td>
                            <span className="text-sm text-secondary-light">
                              {item.available_n_kg_ha ?? "—"} / {item.available_p2o5_kg_ha ?? "—"} / {item.available_k2o_kg_ha ?? "—"}
                            </span>
                          </td>

                          <td className="text-center">
                            {isNormal ? (
                              <span className="px-16 py-4 radius-4 fw-medium text-sm bg-success-focus text-success-600 border border-success-main">
                                Normal
                              </span>
                            ) : (
                              <div className="d-flex align-items-center justify-content-center flex-wrap gap-1">
                                {item.is_acidic && (
                                  <span className="px-8 py-2 radius-4 fw-medium text-xs bg-danger-focus text-danger-600 border border-danger-main">
                                    Acidic
                                  </span>
                                )}
                                {item.is_saline_sodic && (
                                  <span className="px-8 py-2 radius-4 fw-medium text-xs bg-warning-focus text-warning-600 border border-warning-main">
                                    Saline-Sodic
                                  </span>
                                )}
                                {item.is_calcareous && (
                                  <span className="px-8 py-2 radius-4 fw-medium text-xs bg-info-focus text-info-600 border border-info-main">
                                    Calcareous
                                  </span>
                                )}
                                {item.heavy_metals_detected && (
                                  <span className="px-8 py-2 radius-4 fw-medium text-xs bg-danger-focus text-danger-600 border border-danger-main">
                                    Metals
                                  </span>
                                )}
                              </div>
                            )}
                          </td>

                          <td className="text-center">
                            <div className="d-flex align-items-center gap-10 justify-content-center">
                              <button
                                type="button"
                                className="edit-btn bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                onClick={() => handleEdit(item)}
                                title="Edit"
                              >
                                <Icon icon="lucide:edit" className="menu-icon" />
                              </button>

                              {user?.IsDeleteAllowed !== false && (
                                <button
                                  type="button"
                                  className="delete-btn bg-danger-focus text-danger-600 bg-hover-danger-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                  onClick={() => confirmDelete(item)}
                                  title="Delete"
                                >
                                  <Icon icon="lucide:trash-2" className="menu-icon" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* ─── Footer / Pagination ─────────────────────────────── */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 m-16">
              <span className="text-secondary-light">
                Showing {totalRecords === 0 ? 0 : startIndex + 1} to {endIndex} of {totalRecords} entries
              </span>

              {totalPages > 1 && (
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-light btn-sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  >
                    Previous
                  </button>
                  <span className="align-self-center text-secondary-light">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    className="btn btn-light btn-sm"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ─── Add/Edit Drawer ────────────────────────────────────────── */}
      <AddGeographicalSoil
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        recordToEdit={recordToEdit}
        onSuccess={fetchRecords}
      />

      {/* ─── Delete Confirmation Modal ──────────────────────────────── */}
      {showDeleteModal && recordToDelete && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.55)", backdropFilter: "blur(2px)", zIndex: 1060 }}
          onClick={closeDeleteModal}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: 440 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: 14 }}>
              <div className="modal-body text-center" style={{ padding: "32px 28px 20px" }}>
                <div
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 18,
                  }}
                >
                  <Icon icon="lucide:trash-2" style={{ fontSize: 26, color: "#dc2626" }} />
                </div>

                <h5
                  className="fw-semibold mb-2"
                  style={{ fontSize: "1.05rem", color: "#0f172a", letterSpacing: "-0.01em" }}
                >
                  Delete this soil record?
                </h5>

                <p
                  className="mb-3"
                  style={{ fontSize: "0.9rem", color: "#64748b", lineHeight: 1.55 }}
                >
                  You are about to delete{" "}
                  <strong style={{ color: "#0f172a", fontWeight: 600 }}>
                    {recordToDelete.entity_name || recordToDelete.level_id}
                  </strong>{" "}
                  <span style={{ color: "#94a3b8" }}>
                    ({recordToDelete.level_name || `Level ${recordToDelete.level_type}`})
                  </span>
                  . This action cannot be undone.
                </p>
              </div>

              <div
                className="modal-footer border-0 d-flex gap-2"
                style={{ padding: "4px 28px 24px" }}
              >
                <button
                  type="button"
                  className="btn flex-fill fw-medium"
                  onClick={closeDeleteModal}
                  disabled={!!deletingId}
                  style={{
                    background: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    color: "#334155",
                    padding: "10px 16px",
                    borderRadius: 8,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn flex-fill fw-medium d-inline-flex align-items-center justify-content-center gap-2"
                  onClick={handleDelete}
                  disabled={!!deletingId}
                  style={{
                    background: "#dc2626",
                    border: "1px solid #dc2626",
                    color: "#fff",
                    padding: "10px 16px",
                    borderRadius: 8,
                  }}
                >
                  {deletingId ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" />
                      <span>Deleting…</span>
                    </>
                  ) : (
                    <>
                      <Icon icon="lucide:trash-2" style={{ fontSize: 16 }} />
                      <span>Delete</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GeographicalSoilList;